import {
  isViewPath,
} from './routes'

/**
 * Where the user was headed when a guard turned them away.
 *
 * Both auth guards in App.tsx redirect with `<Navigate>`, which carries no
 * search string: `ProtectedRoute` sends a signed-out visitor to /login,
 * `SetupGuard` sends one who hasn't finished onboarding to /onboarding. So
 * every deep link we hand out — `?connect_app=`, `?connect=`,
 * `?create_webhook=1&name=…`, `?add_inbox=1` — silently loses its params on the
 * way in, and the page that eventually mounts renders something else. User 3574
 * followed an Odoo connect link twice, was bounced to the onboarding wizard both
 * times, and never once reached the integrations page (GET-13956).
 *
 * One stash for all of them, rather than a per-parameter rescue: what is lost is
 * always the same thing — the URL — so what is remembered is the URL.
 *
 * Session-scoped like its neighbours (`catch_checkout_intent`,
 * `catch_portal_intent`), because it is only ever needed between a bounce and
 * the redirect that follows it. A path is stored only if a guard put it there,
 * and it is dropped the moment it is used, so a stale one cannot hijack a later
 * unrelated sign-in.
 */

const PENDING_ROUTE_KEY = 'catch_pending_route'

/** Remember a guarded route, called immediately before the guard redirects. */
export function rememberPendingRoute(pathname: string, search: string): void {
  // A bare path usually carries no intent worth resuming, and storing one would
  // only override a destination the backend deliberately chose (`?source=`).
  // An assistant view is the exception: its id is in the path, and a view link
  // opened signed out must land on that view after sign-in (GET-15593).
  if (!search && !isViewPath(pathname)) return
  sessionStorage.setItem(PENDING_ROUTE_KEY, `${pathname}${search}`)
}

/** The remembered route, if any. Leaves it in place for a later consumer. */
export function peekPendingRoute(): string | null {
  return sessionStorage.getItem(PENDING_ROUTE_KEY)
}

/** Drop it, once the user has actually been sent there. */
export function clearPendingRoute(): void {
  sessionStorage.removeItem(PENDING_ROUTE_KEY)
}

/**
 * Whether a remembered URL is about the same page the sign-in already named.
 *
 * The stash lives for the browser session, so an abandoned bounce earlier in
 * the same tab leaves one behind — and a later `/go?dest=checkout` recipient
 * must not be sent to it. A remembered URL may add detail to the page a link
 * named (its query string is the whole point) but may never replace the page.
 * With nothing named, there is nothing to disagree with and it stands alone.
 */
export function samePage(pending: string, named: string | null): boolean {
  if (named === null) return true
  return pending.split('?')[0] === named.split('?')[0]
}
